import * as React from 'react';

/**
 * Counts down to zero from `seconds`, restartable.
 *
 * Drives OTP resend cooldowns. Ticks against a wall-clock deadline rather than
 * decrementing a counter, so a backgrounded tab (where timers are throttled)
 * resumes with the correct remaining time instead of a frozen one.
 */
export function useCountdown(seconds: number) {
  const [deadline, setDeadline] = React.useState<number | null>(null);
  const [remaining, setRemaining] = React.useState(0);

  React.useEffect(() => {
    if (deadline === null) return;

    const tick = () => {
      const left = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setRemaining(left);
      if (left === 0) setDeadline(null);
    };

    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [deadline]);

  const start = React.useCallback(
    (overrideSeconds?: number) => {
      setDeadline(Date.now() + (overrideSeconds ?? seconds) * 1000);
    },
    [seconds],
  );

  const reset = React.useCallback(() => {
    setDeadline(null);
    setRemaining(0);
  }, []);

  return { remaining, isRunning: remaining > 0, start, reset } as const;
}
